import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createWine, updateWine, getWine, getVineyards, uploadFile, suggestPairings } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Upload, X, Image as ImageIcon, Plus, Sparkles } from 'lucide-react';

const WineForm = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, canAddContent, loading: authLoading } = useAuth();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [vineyards, setVineyards] = useState([]);
  const [pairingInput, setPairingInput] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    vineyard_id: searchParams.get('vineyard') || '',
    grape_variety: '',
    vintage: '',
    description: '',
    tasting_notes: '',
    aroma: '',
    flavor: '',
    finish: '',
    food_pairings: [],
    alcohol_content: '',
    price_range: '',
    photos: [],
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!canAddContent) {
      toast.error('Your account type does not allow adding content');
      navigate('/wines');
      return;
    }
    loadVineyards();
    if (isEdit) {
      loadWine();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAuthenticated, canAddContent, isEdit, navigate, authLoading]);

  const loadVineyards = async () => {
    try {
      const data = await getVineyards({ limit: 100 });
      setVineyards(data);
    } catch (error) {
      console.error('Failed to load vineyards:', error);
    }
  };

  const loadWine = async () => {
    try {
      const data = await getWine(id);
      setFormData({
        ...data,
        vintage: data.vintage || '',
        alcohol_content: data.alcohol_content || '',
      });
    } catch (error) {
      toast.error('Failed to load wine');
      navigate('/wines');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const urls = await Promise.all(files.map((file) => uploadFile(file)));
      setFormData((prev) => ({
        ...prev,
        photos: [...prev.photos, ...urls],
      }));
      toast.success('Photo(s) uploaded');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const addPhotoUrl = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      setFormData((prev) => ({
        ...prev,
        photos: [...prev.photos, url],
      }));
    }
  };

  const removePhoto = (index) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const addPairing = () => {
    if (pairingInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        food_pairings: [...prev.food_pairings, pairingInput.trim()],
      }));
      setPairingInput('');
    }
  };

  const removePairing = (index) => {
    setFormData((prev) => ({
      ...prev,
      food_pairings: prev.food_pairings.filter((_, i) => i !== index),
    }));
  };

  const handleSuggestPairings = async () => {
    if (!formData.grape_variety) {
      toast.error('Select a grape variety first');
      return;
    }
    setSuggesting(true);
    try {
      const suggestions = await suggestPairings({
        grape_variety: formData.grape_variety,
        wine_name: formData.name,
        aroma: formData.aroma,
        flavor: formData.flavor,
        finish: formData.finish,
        tasting_notes: formData.tasting_notes,
        vintage: formData.vintage ? parseInt(formData.vintage) : null,
      });
      setFormData((prev) => {
        const existing = new Set(prev.food_pairings.map(p => p.toLowerCase()));
        const added = suggestions.filter(s => !existing.has(s.toLowerCase()));
        return { ...prev, food_pairings: [...prev.food_pairings, ...added] };
      });
      toast.success(`Added ${suggestions.length} AI pairing suggestions`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not generate pairings');
    } finally {
      setSuggesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.vineyard_id || !formData.grape_variety) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        vintage: formData.vintage ? parseInt(formData.vintage) : null,
        alcohol_content: formData.alcohol_content ? parseFloat(formData.alcohol_content) : null,
      };

      if (isEdit) {
        await updateWine(id, submitData);
        toast.success('Wine updated');
      } else {
        const created = await createWine(submitData);
        toast.success('Wine created');
        navigate(`/wines/${created.id}`);
        return;
      }
      navigate(`/wines/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const grapeVarieties = [
    'Cabernet Sauvignon', 'Merlot', 'Pinot Noir', 'Chardonnay', 'Sauvignon Blanc',
    'Riesling', 'Syrah/Shiraz', 'Tempranillo', 'Malbec', 'Zinfandel',
    'Grenache', 'Sangiovese', 'Nebbiolo', 'Gewürztraminer', 'Viognier',
    'Pinot Grigio', 'Chenin Blanc', 'Cabernet Franc', 'Petit Verdot', 'Other'
  ];

  return (
    <div className="min-h-screen py-8" data-testid="wine-form-page">
      <div className="container mx-auto px-4 md:px-8 max-w-3xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-stone-300 mb-8 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="text-4xl font-serif text-stone-50 mb-8">
          {isEdit ? 'Edit Wine' : 'Add New Wine'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="space-y-6 p-6 bg-stone-900/30 border border-white/5">
            <h2 className="text-xs uppercase tracking-widest text-[#d4af37]">Basic Information</h2>
            
            <div className="space-y-2">
              <Label htmlFor="name" className="text-stone-400">
                Wine Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="bg-stone-900/50 border-stone-700"
                placeholder="e.g., Grand Cru Classé 2018"
                data-testid="name-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="vineyard_id" className="text-stone-400">
                  Vineyard <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.vineyard_id}
                  onValueChange={(v) => handleSelectChange('vineyard_id', v)}
                >
                  <SelectTrigger className="bg-stone-900/50 border-stone-700" data-testid="vineyard-select">
                    <SelectValue placeholder="Select vineyard" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700">
                    {vineyards.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="grape_variety" className="text-stone-400">
                  Grape Variety <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.grape_variety}
                  onValueChange={(v) => handleSelectChange('grape_variety', v)}
                >
                  <SelectTrigger className="bg-stone-900/50 border-stone-700" data-testid="grape-variety-select">
                    <SelectValue placeholder="Select grape variety" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700">
                    {grapeVarieties.map((g) => (
                      <SelectItem key={g} value={g}>
                        {g}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vintage" className="text-stone-400">Vintage Year</Label>
                <Input
                  id="vintage"
                  name="vintage"
                  type="number"
                  min="1900"
                  max={new Date().getFullYear()}
                  value={formData.vintage}
                  onChange={handleChange}
                  className="bg-stone-900/50 border-stone-700"
                  placeholder="e.g., 2018"
                  data-testid="vintage-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="alcohol_content" className="text-stone-400">Alcohol %</Label>
                <Input
                  id="alcohol_content"
                  name="alcohol_content"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={formData.alcohol_content}
                  onChange={handleChange}
                  className="bg-stone-900/50 border-stone-700"
                  placeholder="e.g., 13.5"
                  data-testid="alcohol-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="price_range" className="text-stone-400">Price Range</Label>
              <Input
                id="price_range"
                name="price_range"
                value={formData.price_range}
                onChange={handleChange}
                className="bg-stone-900/50 border-stone-700"
                placeholder="e.g., $50-100"
                data-testid="price-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-stone-400">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="bg-stone-900/50 border-stone-700"
                placeholder="Describe the wine..."
                data-testid="description-input"
              />
            </div>
          </div>

          {/* Tasting Characteristics */}
          <div className="space-y-6 p-6 bg-stone-900/30 border border-white/5">
            <h2 className="text-xs uppercase tracking-widest text-[#d4af37]">Tasting Characteristics</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="aroma" className="text-stone-400">Aroma</Label>
                <Input
                  id="aroma"
                  name="aroma"
                  value={formData.aroma}
                  onChange={handleChange}
                  className="bg-stone-900/50 border-stone-700"
                  placeholder="e.g., Blackberry, vanilla, oak"
                  data-testid="aroma-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="flavor" className="text-stone-400">Flavor</Label>
                <Input
                  id="flavor"
                  name="flavor"
                  value={formData.flavor}
                  onChange={handleChange}
                  className="bg-stone-900/50 border-stone-700"
                  placeholder="e.g., Rich, complex, fruity"
                  data-testid="flavor-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="finish" className="text-stone-400">Finish</Label>
              <Input
                id="finish"
                name="finish"
                value={formData.finish}
                onChange={handleChange}
                className="bg-stone-900/50 border-stone-700"
                placeholder="e.g., Long, smooth, tannic"
                data-testid="finish-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tasting_notes" className="text-stone-400">Tasting Notes</Label>
              <Textarea
                id="tasting_notes"
                name="tasting_notes"
                value={formData.tasting_notes}
                onChange={handleChange}
                rows={3}
                className="bg-stone-900/50 border-stone-700"
                placeholder="Detailed tasting notes..."
                data-testid="tasting-notes-input"
              />
            </div>
          </div>

          {/* Food Pairings */}
          <div className="space-y-6 p-6 bg-stone-900/30 border border-white/5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="text-xs uppercase tracking-widest text-[#d4af37]">Food Pairings</h2>
              <Button
                type="button"
                onClick={handleSuggestPairings}
                disabled={suggesting || !formData.grape_variety}
                variant="outline"
                className="border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 hover:text-[#d4af37] rounded-none uppercase tracking-widest text-xs"
                data-testid="ai-suggest-pairings-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {suggesting ? 'Thinking…' : 'AI Suggest'}
              </Button>
            </div>
            <p className="text-xs text-stone-500 -mt-2">
              Add your own pairings, or let our AI sommelier suggest some based on the wine's profile.
            </p>
            
            <div className="flex gap-2">
              <Input
                value={pairingInput}
                onChange={(e) => setPairingInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPairing())}
                className="bg-stone-900/50 border-stone-700"
                placeholder="e.g., Grilled steak, aged cheese"
                data-testid="pairing-input"
              />
              <Button
                type="button"
                onClick={addPairing}
                variant="outline"
                className="border-stone-700"
                data-testid="add-pairing-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {formData.food_pairings.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.food_pairings.map((pairing, index) => (
                  <span
                    key={`pairing-${pairing}-${index}`}
                    className="flex items-center gap-2 px-3 py-1 bg-stone-800 border border-stone-700 text-stone-300 text-sm"
                  >
                    {pairing}
                    <button
                      type="button"
                      onClick={() => removePairing(index)}
                      className="text-stone-500 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Photos */}
          <div className="space-y-6 p-6 bg-stone-900/30 border border-white/5">
            <h2 className="text-xs uppercase tracking-widest text-[#d4af37] flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Photos
            </h2>
            
            <div className="flex gap-4 flex-wrap">
              {formData.photos.map((photo, index) => (
                <div key={`photo-${photo.substring(0, 20)}-${index}`} className="relative w-24 h-24 group">
                  <img
                    src={photo}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-full object-cover border border-stone-700"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
              
              <label className="w-24 h-24 border-2 border-dashed border-stone-700 flex flex-col items-center justify-center cursor-pointer hover:border-[#d4af37] transition-colors">
                <Upload className="w-6 h-6 text-stone-600 mb-1" />
                <span className="text-xs text-stone-600">Upload</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  data-testid="photo-upload"
                />
              </label>

              <button
                type="button"
                onClick={addPhotoUrl}
                className="w-24 h-24 border-2 border-dashed border-stone-700 flex flex-col items-center justify-center hover:border-[#d4af37] transition-colors"
                data-testid="add-photo-url-btn"
              >
                <Plus className="w-6 h-6 text-stone-600 mb-1" />
                <span className="text-xs text-stone-600">Add URL</span>
              </button>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex-1 border-stone-700 rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || uploading}
              className="flex-1 bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs"
              data-testid="submit-btn"
            >
              {loading ? 'Saving...' : isEdit ? 'Update Wine' : 'Create Wine'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WineForm;
